import * as React from "react";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { useOrganization } from "@/contexts/OrganizationContext";
import {
  CategoryFilter,
  type CategoryFilterValue,
} from "./components/CategoryFilter";
import {
  createDefaultUsageViewPreferences,
  CUSTOMIZABLE_SECTION_IDS,
  type CustomizableSectionId,
  type UsageViewPreferences,
} from "./components/CustomizeViewDrawer";
import { FiltersBar } from "./components/FiltersBar";
import { StatCard } from "./components/StatCard";
import { TotalsOverTime } from "./components/TotalsOverTime";
import { DailySpend } from "./components/DailySpend";
import { SpendPerProject } from "./components/SpendPerProject";
import { TopList } from "./components/TopList";
import { ProviderUsage } from "./components/ProviderUsage";
import { LlmConfigsTable } from "./components/LlmConfigsTable";
import { TopDeployments } from "./components/TopDeployments";
import { TotalGpuHoursCard } from "./components/TotalGpuHoursCard";
import { Badge } from "@/components/ui/badge";
import {
  calculateQuotaUsagePercent,
  calculateRawUsagePercent,
  formatCompactNumber,
  formatCurrency,
  formatNumber,
  formatPlanCurrencyLimit,
  formatPlanLimit,
} from "./utils";
import {
  useGpuStats,
  useRequestStats,
  useOrganizationSubscription,
  useSpendStats,
  useTokenStats,
  useTopApiKeys,
  useTopFlows,
  useTopModels,
  useUsageStats,
  useUserPlan,
  type StatsFilters,
  type MetricScope,
  type TopApiKey,
  type TopFlow,
  type TopModel,
} from "./api";

type Category = Exclude<CategoryFilterValue, "summary">;

type Visible = Category | "deployments" | "all";

function show(filter: CategoryFilterValue, card: Visible): boolean {
  if (filter === "summary") return true;
  if (card === "all") return true;
  return card === filter;
}

const toSeconds = (d: Date) => Math.floor(d.getTime() / 1000);

const cardTransition = {
  layout: { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const },
  opacity: { duration: 0.2 },
};

const cardMotion = {
  layout: true,
  initial: { opacity: 0, y: 8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, transition: { duration: 0 } },
  transition: cardTransition,
};

const UsageAndCost = () => {
  const { t } = useTranslation();
  const { currentOrganization } = useOrganization();
  const [filter, setFilter] = React.useState<CategoryFilterValue>("summary");
  const [metricScope, setMetricScope] =
    React.useState<MetricScope>("usage_and_cost");
  const [viewPreferences, setViewPreferences] = React.useState(
    createDefaultUsageViewPreferences,
  );
  const [startDate, setStartDate] = React.useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = React.useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [userId, setUserId] = React.useState("all");
  const [projectIds, setProjectIds] = React.useState<string[]>([]);
  const organizationId =
    currentOrganization?.id ||
    (typeof window !== "undefined"
      ? localStorage.getItem("currentOrganizationId")
      : null);
  const showDeploymentMetrics = Boolean(organizationId);

  React.useEffect(() => {
    if (!showDeploymentMetrics && filter === "deployments") {
      setFilter("summary");
    }
  }, [filter, showDeploymentMetrics]);

  const organizationSubscription = useOrganizationSubscription(organizationId);
  const userPlan = useUserPlan(!organizationId);
  const plan = organizationId ? organizationSubscription.data : userPlan.data;
  const planName = plan?.name;

  // Default the picker to the current billing period once the plan loads.
  // Applied only once so it never overrides a range the user picks later.
  const [billingDatesReady, setBillingDatesReady] = React.useState(false);
  const planPending = organizationId
    ? organizationSubscription.isPending
    : userPlan.isPending;
  React.useEffect(() => {
    if (billingDatesReady || planPending) return;
    if (plan?.period_start && plan?.period_end) {
      const periodStart = new Date(plan.period_start);
      const periodEnd = new Date(plan.period_end);
      if (
        !Number.isNaN(periodStart.getTime()) &&
        !Number.isNaN(periodEnd.getTime())
      ) {
        periodStart.setMilliseconds(0);
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);
        if (periodEnd > todayEnd) {
          periodEnd.setTime(todayEnd.getTime());
        } else {
          periodEnd.setMilliseconds(0);
        }
        setStartDate(periodStart);
        setEndDate(periodEnd);
      }
    }
    // An absent/invalid billing period (or failed plan request) uses the picker defaults.
    setBillingDatesReady(true);
  }, [billingDatesReady, planPending, plan?.period_start, plan?.period_end]);

  const outsideBillingPeriod = React.useMemo(() => {
    if (!plan?.period_start || !plan?.period_end) return false;
    const periodStart = new Date(plan.period_start);
    const periodEnd = new Date(plan.period_end);
    if (
      Number.isNaN(periodStart.getTime()) ||
      Number.isNaN(periodEnd.getTime())
    )
      return false;
    return (
      toSeconds(startDate) < toSeconds(periodStart) ||
      toSeconds(endDate) > toSeconds(periodEnd)
    );
  }, [plan?.period_start, plan?.period_end, startDate, endDate]);

  const filters: StatsFilters = React.useMemo(
    () => ({
      startDate,
      endDate,
      userIds: userId === "all" ? [] : [userId],
      projectIds,
    }),
    [startDate, endDate, userId, projectIds],
  );

  const dailySpend = React.useMemo<{
    hidden: boolean;
    filters: StatsFilters;
  }>(() => {
    if (!plan?.period_start || !plan?.period_end) {
      return { hidden: false, filters };
    }
    const periodStart = new Date(plan.period_start);
    const periodEnd = new Date(plan.period_end);
    if (
      Number.isNaN(periodStart.getTime()) ||
      Number.isNaN(periodEnd.getTime())
    ) {
      return { hidden: false, filters };
    }

    if (
      toSeconds(endDate) < toSeconds(periodStart) ||
      toSeconds(startDate) > toSeconds(periodEnd)
    ) {
      return { hidden: true, filters };
    }

    const clampedStart =
      toSeconds(startDate) < toSeconds(periodStart) ? periodStart : startDate;
    const clampedEnd =
      toSeconds(endDate) > toSeconds(periodEnd) ? periodEnd : endDate;
    return {
      hidden: false,
      filters: { ...filters, startDate: clampedStart, endDate: clampedEnd },
    };
  }, [plan?.period_start, plan?.period_end, startDate, endDate, filters]);

  return (
    <DashboardLayout title={t("usageAndCost.title")}>
      <div className="flex flex-col gap-4 pb-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CategoryFilter
            value={filter}
            onChange={setFilter}
            showDeployments={showDeploymentMetrics}
          />
          {planName && (
            <Badge
              variant="outline"
              className="rounded-xl bg-card px-4 py-1.5 text-sm"
            >
              <span className="text-muted-foreground">
                {t("usageAndCost.tier")}
              </span>
              <span className="ms-1 font-medium text-foreground">
                {planName}
              </span>
            </Badge>
          )}
        </div>

        <FiltersBar
          startDate={startDate}
          endDate={endDate}
          onDateChange={(s, e) => {
            setBillingDatesReady(true);
            setStartDate(s);
            setEndDate(e);
          }}
          userId={userId}
          onUserChange={setUserId}
          projectIds={projectIds}
          onProjectChange={setProjectIds}
          metricScope={metricScope}
          onMetricScopeChange={setMetricScope}
          viewPreferences={viewPreferences}
          onViewPreferencesChange={setViewPreferences}
        />

        {outsideBillingPeriod && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm text-amber-600 dark:text-amber-400">
            <AlertTriangle className="size-4 shrink-0" aria-hidden />
            <span>{t("usageAndCost.billingPeriodWarning")}</span>
          </div>
        )}

        <LayoutGroup>
          <FilteredSummary
            filter={filter}
            filters={filters}
            organizationId={organizationId}
            showDeploymentMetrics={showDeploymentMetrics}
            gpuFiltersReady={billingDatesReady}
            hideUsageDetails={outsideBillingPeriod}
            viewPreferences={viewPreferences}
            metricScope={metricScope}
            dailySpendFilters={dailySpend.filters}
            dailySpendHidden={dailySpend.hidden}
          />
        </LayoutGroup>
      </div>
    </DashboardLayout>
  );
};

type StatItem = {
  id: string;
  category: Visible;
  node: React.ReactNode;
};

type SectionItem = {
  id: string;
  category: Visible;
  fullWidth?: boolean;
  node: React.ReactNode;
};

const PERCENT_METRIC_CARDS = new Set([
  "total-spend",
  "total-runtime-cost",
  "total-queries-cost",
]);

function FilteredSummary({
  filter,
  filters,
  organizationId,
  showDeploymentMetrics = false,
  gpuFiltersReady = true,
  hideUsageDetails = false,
  viewPreferences,
  metricScope,
  dailySpendFilters,
  dailySpendHidden = false,
}: {
  filter: CategoryFilterValue;
  filters: StatsFilters;
  organizationId: string | null;
  showDeploymentMetrics?: boolean;
  gpuFiltersReady?: boolean;
  hideUsageDetails?: boolean;
  viewPreferences: UsageViewPreferences;
  metricScope: MetricScope;
  dailySpendFilters?: StatsFilters;
  dailySpendHidden?: boolean;
}) {
  const { t } = useTranslation();
  const spend = useSpendStats(filters, metricScope !== "usage");
  const requests = useRequestStats(filters);
  const tokens = useTokenStats(filters);
  const usage = useUsageStats(filters);
  const [gpuLimit, setGpuLimit] = React.useState(5);
  const gpuSummaryVisible =
    show(filter, "deployments") && showDeploymentMetrics;
  const gpuSectionVisible =
    viewPreferences.sections["top-deployments"] &&
    show(filter, "deployments") &&
    showDeploymentMetrics;
  const gpu = useGpuStats(
    filters,
    organizationId,
    gpuLimit,
    gpuFiltersReady && (gpuSummaryVisible || gpuSectionVisible),
  );
  const totalSpend = spend.data?.total_spend ?? 0;
  const totalSpendLimit = spend.data?.limits?.total_spend;

  const stats: StatItem[] = [
    {
      id: "total-spend",
      category: "all",
      node: (
        <StatCard
          label={t("usageAndCost.stats.totalSpend")}
          value={formatCurrency(totalSpend)}
          displayValueIsZero={formatCurrency(totalSpend) === "$0.00"}
          used={totalSpend}
          limit={totalSpendLimit}
          limitLabel={
            typeof totalSpendLimit === "number" && totalSpendLimit > 0
              ? formatPlanCurrencyLimit(totalSpendLimit)
              : undefined
          }
          resetDate={spend?.data?.billing_period?.end_date}
          category={t("usageAndCost.categories.all")}
          loading={spend.isLoading}
          showProgressTrack
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
    {
      id: "total-requests",
      category: "apis",
      node: (
        <StatCard
          label={t("usageAndCost.stats.totalRequests")}
          value={formatCompactNumber(requests.data?.total_requests ?? 0)}
          valueTooltip={formatNumber(requests.data?.total_requests ?? 0)}
          used={requests.data?.total_requests ?? 0}
          limit={requests.data?.limits?.used_requests}
          limitLabel={
            typeof requests.data?.limits?.used_requests === "number" &&
            requests.data?.limits?.used_requests > 0
              ? formatPlanLimit(requests.data?.limits?.used_requests)
              : undefined
          }
          resetDate={requests?.data?.billing_period?.end_date}
          category={t("usageAndCost.categories.apis")}
          loading={requests.isLoading}
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
    {
      id: "total-queries",
      category: "rags",
      node: (
        <StatCard
          label={t("usageAndCost.stats.totalQueries")}
          value={formatNumber(usage.data?.total_queries ?? 0)}
          used={usage.data?.total_queries ?? 0}
          limit={usage.data?.limits?.total_queries}
          limitLabel={
            typeof usage.data?.limits?.total_queries === "number" &&
            usage.data?.limits?.total_queries > 0
              ? formatPlanLimit(usage.data?.limits?.total_queries)
              : undefined
          }
          resetDate={usage.data?.billing_period?.end_date}
          category={t("usageAndCost.categories.queries")}
          loading={usage.isLoading}
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
    {
      id: "total-tokens",
      category: "models",
      node: (
        <StatCard
          label={t("usageAndCost.stats.totalTokens")}
          value={formatCompactNumber(tokens.data?.total_tokens ?? 0)}
          valueTooltip={formatNumber(tokens.data?.total_tokens ?? 0)}
          used={tokens.data?.spent_usd ?? 0}
          limit={tokens.data?.limits?.budget_usd}
          // limitLabel={
          //   typeof tokens.data?.limits?.budget_usd === "number" &&
          //   tokens.data?.limits?.budget_usd > 0
          //     ? formatPlanCurrencyLimit(tokens.data?.limits?.budget_usd)
          //     : undefined
          // }
          resetDate={tokens.data?.billing_period?.end_date}
          category={t("usageAndCost.categories.models")}
          loading={tokens.isLoading}
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
    {
      id: "total-gpu-hours",
      category: "deployments",
      node: <TotalGpuHoursCard gpu={gpu} hideUsageDetails={hideUsageDetails} />,
    },
    {
      id: "successful-requests",
      category: "apis",
      node: (
        <StatCard
          label={t("usageAndCost.stats.successfulRequests")}
          value={formatNumber(requests.data?.success_requests ?? 0)}
          used={requests.data?.success_requests ?? 0}
          limit={requests.data?.limits?.used_requests}
          limitLabel={
            typeof requests.data?.limits?.used_requests === "number" &&
            requests.data?.limits?.used_requests > 0
              ? formatPlanLimit(requests.data?.limits?.used_requests)
              : undefined
          }
          indicator="success"
          resetDate={requests?.data?.billing_period?.end_date}
          category={t("usageAndCost.categories.apis")}
          loading={requests.isLoading}
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
    {
      id: "failed-requests",
      category: "apis",
      node: (
        <StatCard
          label={t("usageAndCost.stats.failedRequests")}
          value={formatNumber(requests.data?.failed_requests ?? 0)}
          used={requests.data?.failed_requests ?? 0}
          limit={requests.data?.limits?.used_requests}
          limitLabel={
            typeof requests.data?.limits?.used_requests === "number" &&
            requests.data?.limits?.used_requests > 0
              ? formatPlanLimit(requests.data?.limits?.used_requests)
              : undefined
          }
          indicator="error"
          resetDate={requests?.data?.billing_period?.end_date}
          category={t("usageAndCost.categories.apis")}
          loading={requests.isLoading}
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
    {
      id: "total-queries-cost",
      category: "rags",
      node: (
        <StatCard
          label={t("usageAndCost.stats.totalQueriesCost")}
          value={formatCurrency(usage.data?.total_queries_cost ?? 0)}
          used={usage.data?.total_queries_cost ?? 0}
          limit={usage.data?.limits?.total_queries_cost}
          limitLabel={
            typeof usage.data?.limits?.total_queries_cost === "number" &&
            usage.data?.limits?.total_queries_cost > 0
              ? formatPlanCurrencyLimit(usage.data?.limits?.total_queries_cost)
              : undefined
          }
          resetDate={usage.data?.billing_period?.end_date}
          category={t("usageAndCost.categories.queries")}
          loading={usage.isLoading}
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
    {
      id: "total-runtime",
      category: "flows",
      node: (
        <StatCard
          label={t("usageAndCost.stats.totalRuntime")}
          value={formatNumber(usage.data?.total_runtime ?? 0)}
          used={usage.data?.total_runtime ?? 0}
          limit={usage.data?.limits?.total_runtime}
          limitLabel={
            typeof usage.data?.limits?.total_runtime === "number" &&
            usage.data?.limits?.total_runtime > 0
              ? formatPlanLimit(usage.data?.limits?.total_runtime)
              : undefined
          }
          resetDate={usage.data?.billing_period?.end_date}
          unit={t("usageAndCost.stats.minutes")}
          category={t("usageAndCost.categories.flows")}
          loading={usage.isLoading}
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
    {
      id: "total-runtime-cost",
      category: "flows",
      node: (
        <StatCard
          label={t("usageAndCost.stats.totalRuntimeCost")}
          value={formatCurrency(usage.data?.total_runtime_cost ?? 0)}
          used={usage.data?.total_runtime_cost ?? 0}
          limit={usage.data?.limits?.total_runtime_cost}
          limitLabel={
            typeof usage.data?.limits?.total_runtime_cost === "number" &&
            usage.data?.limits?.total_runtime_cost > 0
              ? formatPlanCurrencyLimit(usage.data?.limits?.total_runtime_cost)
              : undefined
          }
          resetDate={usage.data?.billing_period?.end_date}
          category={t("usageAndCost.categories.flows")}
          loading={usage.isLoading}
          showPercentage={viewPreferences.showPercentage && !hideUsageDetails}
        />
      ),
    },
  ];

  const sections: SectionItem[] = [
    {
      id: "totals-over-time",
      category: "all",
      node: <TotalsOverTime filters={filters} />,
    },
    {
      id: "daily-spend",
      category: "all",
      node: <DailySpend filters={dailySpendFilters ?? filters} />,
    },
    {
      id: "spend-per-project",
      category: "all",
      fullWidth: true,
      node: <SpendPerProject filters={filters} metricScope={metricScope} />,
    },
    {
      id: "top-keys",
      category: "apis",
      node: (
        <TopList<TopApiKey>
          title={t("usageAndCost.topKeys.title")}
          category={t("usageAndCost.categories.apis")}
          useData={(limit) => useTopApiKeys(filters, limit, metricScope)}
          rowKey={(r) => `${r.project_id}:${r.api_key_name}`}
          chartKey={metricScope === "usage" ? "total_calls" : "total_spend"}
          chartLabelKey="api_key_name"
          chartLabelFormatter={(v) =>
            metricScope === "usage" ? formatNumber(v) : formatCurrency(v)
          }
          columns={[
            {
              key: "api_key_name",
              header: t("usageAndCost.topKeys.keyAlias"),
              accessor: (r) => r.api_key_name,
            },
            {
              key: "successful_requests",
              header: t("usageAndCost.topKeys.successful"),
              align: "end",
              accessor: (r) => (
                <span className="text-emerald-400">
                  {formatNumber(r.successful_requests)}
                </span>
              ),
            },
            {
              key: "failed_requests",
              header: t("usageAndCost.topKeys.failed"),
              align: "end",
              accessor: (r) => (
                <span className="text-destructive">
                  {formatNumber(r.failed_requests)}
                </span>
              ),
            },
            ...(metricScope === "usage"
              ? []
              : [
                  {
                    key: "total_spend",
                    header: t("usageAndCost.topKeys.spendUsd"),
                    align: "end" as const,
                    accessor: (r: TopApiKey) =>
                      formatCurrency(r.total_spend ?? 0),
                  },
                ]),
          ]}
        />
      ),
    },
    {
      id: "top-models",
      category: "models",
      node: (
        <TopList<TopModel>
          title={t("usageAndCost.topModels.title")}
          category={t("usageAndCost.categories.models")}
          useData={(limit) => useTopModels(filters, limit, metricScope)}
          rowKey={(r) => r.llm_config_id}
          chartKey={metricScope === "usage" ? "total_requests" : "total_spend"}
          chartLabelKey="model"
          chartLabelFormatter={(v) =>
            metricScope === "usage" ? formatNumber(v) : formatCurrency(v)
          }
          columns={[
            {
              key: "model",
              header: t("usageAndCost.topModels.model"),
              accessor: (r) => (
                <span
                  className="block max-w-[260px] truncate"
                  title={r.model_id}
                >
                  {r.model}
                </span>
              ),
            },
            ...(metricScope === "usage"
              ? []
              : [
                  {
                    key: "total_spend",
                    header: t("usageAndCost.topModels.spendUsd"),
                    align: "end" as const,
                    accessor: (r: TopModel) =>
                      formatCurrency(r.total_spend ?? 0),
                  },
                ]),
            {
              key: "successful_requests",
              header: t("usageAndCost.topModels.successful"),
              align: "end",
              accessor: (r) => (
                <span className="text-emerald-400">
                  {formatNumber(r.successful_requests)}
                </span>
              ),
            },
            {
              key: "failed_requests",
              header: t("usageAndCost.topModels.failed"),
              align: "end",
              accessor: (r) => (
                <span className="text-destructive">
                  {formatNumber(r.failed_requests)}
                </span>
              ),
            },
            {
              key: "total_tokens",
              header: t("usageAndCost.topModels.tokens"),
              align: "end",
              accessor: (r) => (
                <span className="text-muted-foreground">
                  {formatCompactNumber(r.total_tokens)}
                </span>
              ),
            },
          ]}
        />
      ),
    },
    {
      id: "top-flows",
      category: "flows",
      node: (
        <TopList<TopFlow>
          title={t("usageAndCost.topFlows.title")}
          category={t("usageAndCost.categories.flows")}
          useData={(limit) => useTopFlows(filters, limit, metricScope)}
          rowKey={(r) => r.flow_id}
          chartKey={metricScope === "usage" ? "total_runs" : "total_spend"}
          chartLabelKey="flow_name"
          chartLabelFormatter={(v) =>
            metricScope === "usage" ? formatNumber(v) : formatCurrency(v)
          }
          columns={[
            {
              key: "flow_name",
              header: t("usageAndCost.topFlows.flowName"),
              accessor: (r) => r.flow_name,
            },
            {
              key: "successful_runs",
              header: t("usageAndCost.topFlows.successful"),
              align: "end",
              accessor: (r) => (
                <span className="text-emerald-400">
                  {formatNumber(r.successful_runs)}
                </span>
              ),
            },
            {
              key: "failed_runs",
              header: t("usageAndCost.topFlows.failed"),
              align: "end",
              accessor: (r) => (
                <span className="text-destructive">
                  {formatNumber(r.failed_runs)}
                </span>
              ),
            },
            {
              key: "total_runs",
              header: t("usageAndCost.topFlows.runCount"),
              align: "end",
              accessor: (r) => formatNumber(r.total_runs),
            },
            {
              key: "total_running_time_ms",
              header: t("usageAndCost.topFlows.runtime"),
              align: "end",
              accessor: (r) =>
                `${Math.round(r.total_running_time_ms / 60000).toLocaleString()} ${t("usageAndCost.topFlows.minutes")}`,
            },
            ...(metricScope === "usage"
              ? []
              : [
                  {
                    key: "total_spend",
                    header: t("usageAndCost.topFlows.spendUsd"),
                    align: "end" as const,
                    accessor: (r: TopFlow) => (
                      <span className="text-muted-foreground">
                        {formatCurrency(r.total_spend ?? 0)}
                      </span>
                    ),
                  },
                ]),
          ]}
        />
      ),
    },
    {
      id: "top-deployments",
      category: "deployments",
      node: (
        <TopDeployments
          gpu={gpu}
          limit={gpuLimit}
          onLimitChange={setGpuLimit}
          metricScope={metricScope}
        />
      ),
    },
    {
      id: "provider-usage",
      category: "all",
      fullWidth: true,
      node: <ProviderUsage filters={filters} metricScope={metricScope} />,
    },
    {
      id: "llm-configs",
      category: "models",
      fullWidth: true,
      node: <LlmConfigsTable filters={filters} metricScope={metricScope} />,
    },
  ];

  const visibleStats = stats.filter(
    (s) =>
      show(filter, s.category) &&
      (showDeploymentMetrics || s.id !== "total-gpu-hours") &&
      !(metricScope === "usage" && PERCENT_METRIC_CARDS.has(s.id)),
  );
  const visibleSections = sections.filter((s) => {
    const customizableId = CUSTOMIZABLE_SECTION_IDS.find(
      (id) => id === s.id,
    ) as CustomizableSectionId | undefined;
    const enabled =
      customizableId === undefined || viewPreferences.sections[customizableId];
    return (
      enabled &&
      show(filter, s.category) &&
      (showDeploymentMetrics || s.id !== "top-deployments") &&
      !(dailySpendHidden && s.id === "daily-spend") &&
      !(metricScope === "usage" && s.id === "daily-spend")
    );
  });

  return (
    <div className="flex flex-col gap-4">
      <motion.div
        layout
        transition={cardTransition}
        className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5"
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {visibleStats.map((s) => (
            <motion.div key={s.id} {...cardMotion} className="h-full">
              {s.node}
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>

      <motion.div
        layout
        transition={cardTransition}
        className="grid grid-cols-1 gap-4 lg:grid-cols-2"
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {visibleSections.map((s) => (
            <motion.div
              key={s.id}
              {...cardMotion}
              className={s.fullWidth ? "h-full lg:col-span-2" : "h-full"}
            >
              {s.node}
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export default UsageAndCost;
